import Chart from 'chart.js/auto';

export function generateChart(context) {
  // Filter data based on selected filters and search query
  const filteredData = context.allData
    .filter(item => {
      const matchesFilters =
        (!context.selectedDate || item.attributes.date === context.selectedDate) &&
        (!context.selectedUpdateDate || item.attributes.todaydate === context.selectedUpdateDate) &&
        (!context.selectedCruiseline || item.attributes.cruiseline === context.selectedCruiseline) &&
        (!context.selectedTourship || item.attributes.tourship === context.selectedTourship) &&
        (!context.selectedTourcode || item.attributes.tourcode === context.selectedTourcode) &&
        (!context.selectedMxpcode || item.attributes.mxpcode === context.selectedMxpcode) &&
        (!context.selectedTourname || item.attributes.tourname === context.selectedTourname) &&
        (!context.selectedYear || new Date(item.attributes.todaydate).getFullYear() === parseInt(context.selectedYear));

      if (!context.searchQuery || !context.searchQuery.trim()) {
        return matchesFilters;
      }

      const searchTerms = context.searchQuery.trim().split(';').map(term => term.trim()).filter(term => term);
      const searchableFields = ['tourname', 'tourcode', 'cruiseline', 'tourship', 'mxpcode'];

      return matchesFilters && searchTerms.some(term => {
        return searchableFields.some(field => {
          const value = item.attributes[field] || '';
          return value.toLowerCase().includes(term.toLowerCase());
        });
      });
    })
    .sort((a, b) => b.attributes.todaydate.localeCompare(a.attributes.todaydate));

  // Generate unique tour names with formatted times (HH:mm)
  const uniqueTourNameTimes = [...new Set(filteredData.map(item => 
    `${item.attributes.tourname} (${item.attributes.fromtime ? item.attributes.fromtime.slice(0, 5) : ''})`
  ))];
  // Sort dates in descending order for pie chart (most recent first)
  const uniqueDatesDesc = [...new Set(filteredData.map(item => item.attributes.todaydate))].sort((a, b) => b.localeCompare(a));
  // Create a copy sorted in ascending order for line chart (earliest first)
  const uniqueDatesAsc = [...uniqueDatesDesc].sort((a, b) => a.localeCompare(b));

  // Define shared colors for both charts
  const tourColors = [
    'blue', 'red', 'green', 'orange', 'purple', 'brown', 'yellow', 
    'lime', 'black', 'cadetblue', 'aqua', 'bisque', 'blueviolet', 
    'chocolate', 'coral', 'darkgreen', 'gold', 'cyan', 'darkcyan',
    'darkblue', 'firebrick', 'indigo', 'khaki', 'lightseagreen',
    'lightskyblue', 'maroon', 'peru', 'plum', 'wheat', 'fuchsia',
    'lavender', 'olive', 'salmon', 'teal', 'magenta', 'turquoise',
    'navy', 'slategray', 'hotpink', 'chartreuse', 'goldenrod',
    'rosybrown', 'cornflowerblue', 'deeppink'
  ];

  // Create datasets for the line chart
  const lineDatasets = uniqueTourNameTimes.map((tourNameTime, index) => {
    const [tourName, fromTime] = tourNameTime.split(' (');
    const formattedFromTime = fromTime.slice(0, -1);
    const filteredItems = filteredData.filter(item => 
      item.attributes.tourname === tourName && 
      (item.attributes.fromtime ? item.attributes.fromtime.slice(0, 5) : '') === formattedFromTime
    );
    return {
      label: tourNameTime,
      data: filteredItems.map(item => ({
        x: item.attributes.todaydate,
        y: item.attributes.totalsold
      })),
      borderColor: tourColors[index % tourColors.length],
      fill: false,
    };
  });

  // Calculate data for the pie chart (totalsold on last update date)
  const lastUpdateDate = uniqueDatesDesc[0] || 'N/A'; // Most recent date
  const pieData = uniqueTourNameTimes.map(tourNameTime => {
    const [tourName, fromTime] = tourNameTime.split(' (');
    const formattedFromTime = fromTime.slice(0, -1);
    const filteredItems = filteredData.filter(item => 
      item.attributes.tourname === tourName && 
      (item.attributes.fromtime ? item.attributes.fromtime.slice(0, 5) : '') === formattedFromTime
    );
    // Find the item with the last update date
    const lastUpdateItem = filteredItems.find(item => item.attributes.todaydate === lastUpdateDate);
    const totalSoldLastUpdate = lastUpdateItem ? lastUpdateItem.attributes.totalsold || 0 : 0;
    return {
      tourNameTime,
      totalSoldLastUpdate // Total sold on the last update date
    };
  });
  // Calculate total sold on last update date
  const totalSoldOverall = pieData.reduce((sum, item) => sum + item.totalSoldLastUpdate, 0);

  // Generate filter legend
  const filterLegend = [
    context.selectedDate && `Date: ${context.selectedDate}`,
    context.selectedCruiseline && `Cruiseline: ${context.selectedCruiseline}`,
    context.selectedTourship && `Tourship: ${context.selectedTourship}`,
    context.selectedTourname && `Tourname: ${context.selectedTourname}`,
    context.selectedTourcode && `Tourcode: ${context.selectedTourcode}`,
    context.selectedMxpcode && `Mxpcode: ${context.selectedMxpcode}`,
    context.selectedYear && `Year: ${context.selectedYear}`,
    context.searchQuery && context.searchQuery.trim() && `Search: ${context.searchQuery.trim()}`
  ].filter(Boolean);

  // Open a new window
  const chartWindow = window.open('', '_blank', 'width=1600,height=800');
  if (!chartWindow) {
    console.error('Failed to open chart window. Ensure pop-ups are not blocked.');
    return;
  }

  // Write HTML content to the new window
  chartWindow.document.write(`
    <html>
      <head>
        <title>Chart Window</title>
        <style>
          body { 
            padding: 20px; 
            font-family: Arial, sans-serif; 
            margin: 0; 
            display: flex; 
            flex-direction: column; 
            align-items: center; 
          }
          .chart-container { 
            display: flex; 
            flex-direction: row; 
            gap: 100px; 
            justify-content: center; 
          }
          .pie-container {
            display: flex;
            flex-direction: column;
            align-items: center;
          }
          .info-box { 
            margin-top: 20px; 
            padding: 15px; 
            border: 1px solid #ccc; 
            border-radius: 5px; 
            background-color: #f9f9f9; 
            width: 300px; 
            box-shadow: 0 2px 5px rgba(0,0,0,0.1);
          }
          .info-box div { 
            margin-bottom: 8px; 
          }
          canvas { 
            width: 700px !important; 
            height: 600px !important; 
          }
        </style>
      </head>
      <body>
        <div class="chart-container">
          <canvas id="lineChartCanvas" width="700" height="600"></canvas>
          <div class="pie-container">
            <canvas id="pieChartCanvas" width="700" height="600"></canvas>
            <div class="info-box">
              <div>Total Sold: ${totalSoldOverall}</div>
              <div>Last Stand: ${lastUpdateDate}</div>
              ${filterLegend.length > 0 ? '<div>Filters:</div>' : ''}
              ${filterLegend.map(filter => `<div>${filter}</div>`).join('')}
            </div>
          </div>
        </div>
      </body>
    </html>
  `);
  chartWindow.document.close();

  // Custom plugin to draw totalsold values inside pie chart slices
  const customDataLabelsPlugin = {
    id: 'customDataLabels',
    afterDatasetsDraw(chart) {
      const { ctx, chartArea, data } = chart;
      ctx.save();
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = '#fff'; // White text for contrast

      chart.getDatasetMeta(0).data.forEach((arc, index) => {
        if (!arc.active && arc.endAngle - arc.startAngle > 0.1) { // Skip tiny slices
          const angle = (arc.startAngle + arc.endAngle) / 2;
          const radius = (arc.outerRadius + arc.innerRadius) / 2;
          const x = arc.x + Math.cos(angle) * radius;
          const y = arc.y + Math.sin(angle) * radius;
          const value = pieData[index].totalSoldLastUpdate; // Use totalsold for last update
          ctx.fillText(value, x, y);
        }
      });
      ctx.restore();
    }
  };

  // Initialize charts after the window is fully loaded
  chartWindow.addEventListener('load', () => {
    try {
      // Initialize line chart
      const lineCanvas = chartWindow.document.getElementById('lineChartCanvas');
      if (!lineCanvas) {
        console.error('Line chart canvas not found.');
        return;
      }
      const lineChart = new Chart(lineCanvas, {
        type: 'line',
        data: {
          labels: uniqueDatesAsc, // Use ascending date order
          datasets: lineDatasets
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          scales: {
            x: {
              title: {
                display: true,
                text: 'Date over the year',
                color: 'black',
                font: { size: 18 }
              }
            },
            y: {
              title: {
                display: true,
                text: 'Total Sold',
                color: 'black',
                font: { size: 18 }
              }
            }
          },
          plugins: {
            legend: {
              display: false // Disable line chart legend
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  const dataset = context.dataset;
                  const dataPoint = context.raw;
                  return `${dataset.label}: ${dataPoint.y}`;
                }
              }
            }
          }
        }
      });

      // Initialize pie chart
      const pieCanvas = chartWindow.document.getElementById('pieChartCanvas');
      if (!pieCanvas) {
        console.error('Pie chart canvas not found.');
        return;
      }
      const pieChart = new Chart(pieCanvas, {
        type: 'pie',
        data: {
          labels: pieData.map(item => item.tourNameTime),
          datasets: [{
            data: pieData.map(item => item.totalSoldLastUpdate), // Use totalsold for last update
            backgroundColor: tourColors.slice(0, pieData.length), // Use same colors as line chart
          }]
        },
        options: {
          responsive: false,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'right',
              onClick: (e, legendItem, legend) => {
                const index = legendItem.index;
                // Toggle visibility for pie chart
                const pieMeta = pieChart.getDatasetMeta(0);
                pieMeta.data[index].hidden = !pieMeta.data[index].hidden;
                // Toggle visibility for line chart
                const lineMeta = lineChart.getDatasetMeta(index);
                lineMeta.hidden = !lineMeta.hidden;
                // Update legend item style
                legendItem.textDecoration = pieMeta.data[index].hidden ? 'line-through' : 'none';
                // Update both charts
                pieChart.update();
                lineChart.update();
              },
              labels: {
                generateLabels: (chart) => {
                  const data = chart.data;
                  return data.labels.map((label, index) => {
                    const meta = chart.getDatasetMeta(0);
                    const hidden = meta.data[index].hidden;
                    return {
                      text: label,
                      fillStyle: tourColors[index % tourColors.length],
                      hidden: hidden,
                      textDecoration: hidden ? 'line-through' : 'none',
                      index: index
                    };
                  });
                }
              }
            },
            tooltip: {
              callbacks: {
                label: function(context) {
                  const label = context.label || '';
                  const value = pieData[context.dataIndex].totalSoldLastUpdate;
                  return `${label}: ${value}`;
                }
              }
            }
          }
        },
        plugins: [customDataLabelsPlugin] // Register custom plugin
      });
    } catch (error) {
      console.error('Error initializing charts:', error);
    }
  }, { once: true });
}